import React from 'react';
import moment from 'moment';
import {merge, concat, pickBy, uniq, filter, sortBy, find } from 'lodash';
import { ExportToCsv } from 'export-to-csv';
import XLSX from 'xlsx';
import whitelist from './CSVExport.json';
import { dateRangeFlat } from './DateRange';
import { percentageValue } from './NumberFormatter';

const timelineKeys = ['period', 'report_date', 'total_income', 'total_quantity'];

export default {
    CSVExport,
    XLSExport,
    CSVHeader,
    safeFileSegment,
    CSVBulkExport,
    expandTrends,
    expandTimeline,
    expandTimelineFlat,
    addChangeFields
}

function prepareData(data, entity) {
    data = data.map(item=>pickBy(item, (v,k)=>(v===null || typeof v!=='object')));
    let keys = [],
        sortedData = [],
        entityWhitelist = [],
        finalWhitelist = [];
    
    if(entity && whitelist[entity]!==undefined) {
        entityWhitelist = concat(whitelist[entity], whitelist.common);
    }  
    
    for(let key of entityWhitelist) {
      finalWhitelist.push(key);
      if(key.endsWith('_income') || key.endsWith('_quantity')) {
        finalWhitelist.push('curr_'+key);
        finalWhitelist.push('prev_'+key);
        finalWhitelist.push(key+'_diff');
        finalWhitelist.push(key+'_change');
      }  
    } 
    
    for(let item of data) {
        for(let key of Object.keys(item)) {
            if(finalWhitelist.length && !finalWhitelist.includes(key)) {
                delete item[key];
            }
            else {
                if(key.endsWith('_date') && Number.isInteger(item[key]))
                    item[key] = moment(item[key]).format('YYYY-MM-DD');
                if(key=='upc')
                    item[key] = String(item[key]);
                keys.push(key);
            }
            /*
            if(item[key]===null)
                item[key]='';
            */
        }
    }
    keys = uniq(keys);
    
    keys = sortBy(keys, (key)=>finalWhitelist.indexOf(key));
        
    for(let item of data) {
        let newItem = {};
        for(let key of keys) {
            if(!item.hasOwnProperty(key)) {
                newItem[key]='';
            }
            else
                newItem[key] = item[key];
        }
        // newItem = addChangeFields(newItem);
        sortedData.push(newItem);
    }

    return sortedData;
}

function CSVBulkExport(data, filename) {
    let wb =  XLSX.utils.book_new();
    data.unshift({'warning': [{
        'These reports are for analytical purposes only and should not be used to pay artist royalties': ""
    }]});
    data.forEach((entity, index) => {
        const entityName = Object.keys(entity)[0];
        const entityValues = Object.values(entity)[0];
        const data = prepareData(entityValues, entityName);
        let ws = XLSX.utils.json_to_sheet(data);
        XLSX.utils.book_append_sheet(wb, ws, entityName);
    });
    XLSX.writeFile(wb, `${filename}.xlsx`); 
    
}

function CSVBulkExportOld(data) {
  const SEPARATOR = "\r\n";

  let csv = "data:text/csv;charset=utf-8,";
  data.forEach((entity, index) => {
    const entityName = Object.keys(entity)[0];
    const entityValues = Object.values(entity)[0];
    const headers = whitelist[entityName] ? whitelist[entityName] : Object.keys(entityValues[0]);
    const headersRow = headers.join(',') + SEPARATOR;

    csv += headersRow;
    entityValues.forEach(entityItem => {
      var result = {};
        for (let key in entityItem){
          if (headers.indexOf(key) > -1){
            result[key] = entityItem[key];
          }
        }
      const values = Object.values(result);
      csv += values.join(',') + SEPARATOR;
    });
  });
  const encodedUri = encodeURI(csv);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", "data.csv");
  document.body.appendChild(link);

  link.click();
}

function CSVExport(data, params={}, entity=null){
    const options = merge({
        fieldSeparator: ',',
        quoteStrings: '"',
        decimalSeparator: '.',
        showLabels: true,
        useTextFile: false,
        useBom: true,
        useKeysAsHeaders: true,
      }, params);

    const csvExporter = new ExportToCsv(options);
    data = prepareData(data, entity);
    csvExporter.generateCsv(data);
}

function XLSExport(data, params, entity=null) {
    data = prepareData(data, entity);
    let wb =  XLSX.utils.book_new();
    let ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, entity);
    XLSX.writeFile(wb, `${params.filename}.xlsx`); 
    
}

function safeFileSegment(name, maxLength = 80) {
    let segment = String(name == null ? '' : name).replace(/[\x00-\x1f\/\\?%*:|"<>]/g, '_').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^[_.]+|[_.]+$/g, '');
    if(segment.length > maxLength)
        segment = segment.slice(0, maxLength).replace(/[_.]+$/g, '');
    return segment;
}

function CSVHeader(entity, sort, filter, parentEntityTitle=null, entityName=null){
    let header = [entity];
    const safeName = safeFileSegment(entityName);
    if(safeName)
        header.push(safeName);
    if(sort)
        header.push(sort);
    if(parentEntityTitle)
        header.unshift(parentEntityTitle);

    if(filter) {
        header.push(moment(filter.dateStart).format('YYYYMMDD'));
        header.push(moment(filter.dateEnd).format('YYYYMMDD'));
    }
    return header.join('-');
}

function expandTrends(data) {
    for(let item of data) {
        if(item.trends) {
            for(let trend of item.trends) {
                item[`${trend.content_type}.${trend.vendor}`]=trend.curr_units;
            }
        }
    }
    
    return data;
}

function expandTimeline(data, timelineKey='stms_by_date') {
    let expandedData = [];
    for(let item of data) {
        const timeline = item[timelineKey];
        for(let dayIndex in timeline) {
            const day = timeline[dayIndex];
            let row = {};
            for(let key of Object.keys(item)) {
                const value = (dayIndex == 0)? item[key] : '';
                row[key] = value;
            }
            for(let key of timelineKeys) {
                const rowKey = key == 'curr_units' ? 'units' : key;
                row[rowKey] = day[key];
            }
            expandedData.push(row);
        }
    }
    
    return expandedData;
    
    
}

function expandTimelineFlat(data, timeseries, IDKey) {
    let expandedData = [];
    for(let item of data) {
        let streams = filter(timeseries, (stream=>{
          return stream[IDKey]==item[IDKey];
        }));
        const {dates, period} = dateRangeFlat(streams);
        for(let dateIndex in dates) {
            const date = dates[dateIndex];
            const stream = find(streams, {report_date: date});
            
            let row = {};
            for(let key of Object.keys(item)) {
                const value = (dateIndex == 0)? item[key] : '';
                row[key] = value;
            }
            for(let key of timelineKeys) {
                const rowKey = key == 'curr_units' ? 'units' : key;
                row[rowKey] = stream[key];
            }
            expandedData.push(row);
        }
    }
    return expandedData;
    
    
}

function addChangeFields(record) {
  const currPrefix = "curr_";
  const prevPrefix = "prev_";
  const headers = Object.keys(record);

  const currFields = headers
    .filter(h => h.startsWith(currPrefix))
    .map(h => h.slice(currPrefix.length));

  const prevFields = new Set(
    headers
      .filter(h => h.startsWith(prevPrefix))
      .map(h => h.slice(prevPrefix.length))
  );

  for (const field of currFields) {
    if (prevFields.has(field)) {
      record[`${field}_change`] = percentageValue(
        record[`${currPrefix}${field}`],
        record[`${prevPrefix}${field}`]
      );
    }
  }

  return record;
}